import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { addPartner, deletePartner, getPartners, updatePartner } from '../services/firestoreService';
import { defaultPartners } from '../data/defaultPartners';
import { Partner, PartnerCategory, PartnerFormData } from '../types';

const categories: { value: PartnerCategory; label: string }[] = [
  { value: 'institution', label: 'Institution' },
  { value: 'fondation', label: 'Fondation' },
  { value: 'commerce', label: 'Commerce' },
];

const acceptedLogoTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

interface PartnerFormProps {
  partner: Partner | null;
  nextOrder: number;
  onClose: () => void;
  onSubmit: (data: PartnerFormData) => Promise<void>;
}

const PartnerForm = ({ partner, nextOrder, onClose, onSubmit }: PartnerFormProps) => {
  const [name, setName] = useState(partner?.name || '');
  const [url, setUrl] = useState(partner?.url || '');
  const [category, setCategory] = useState<PartnerCategory>(partner?.category || 'commerce');
  const [order, setOrder] = useState(partner?.order ?? nextOrder);
  const [logo, setLogo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(partner?.logo || null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleLogo = (file: File | undefined) => {
    if (!file) return;
    if (!acceptedLogoTypes.includes(file.type)) {
      setError('Format non supporté : PNG, JPG, WebP ou SVG.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Le logo ne doit pas dépasser 5 Mo.');
      return;
    }
    setError('');
    setLogo(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      setError('Le nom doit contenir au moins 2 caractères.');
      return;
    }
    const trimmedUrl = url.trim();
    if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
      setError('Le lien doit commencer par http:// ou https://');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        name: DOMPurify.sanitize(trimmedName),
        url: trimmedUrl,
        category,
        order: Number(order) || 0,
        logo,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="panel w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <h2 className="heading text-2xl">
          {partner ? 'Modifier le partenaire' : 'Ajouter un partenaire'}
        </h2>

        <label className="block">
          <span className="block font-semibold text-ink mb-1">Nom</span>
          <input value={name} onChange={e => setName(e.target.value)} className="input" required />
        </label>

        <label className="block">
          <span className="block font-semibold text-ink mb-1">Site web <span className="font-normal text-ink/50">(optionnel)</span></span>
          <input type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://" className="input" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block font-semibold text-ink mb-1">Catégorie</span>
            <select value={category} onChange={e => setCategory(e.target.value as PartnerCategory)} className="input">
              {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block font-semibold text-ink mb-1">Ordre</span>
            <input type="number" value={order} onChange={e => setOrder(Number(e.target.value))} className="input" />
          </label>
        </div>

        <div>
          <span className="block font-semibold text-ink mb-1">Logo</span>
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-ink/20 rounded-xl p-4 cursor-pointer hover:border-accent">
            {preview ? (
              <img src={preview} alt="Aperçu du logo" className="max-h-24 object-contain bg-white rounded p-2" />
            ) : (
              <span className="text-sm text-ink/60">Choisir un fichier (PNG, SVG, JPG, WebP · 5 Mo max)</span>
            )}
            <input type="file" accept={acceptedLogoTypes.join(',')} className="sr-only" onChange={e => handleLogo(e.target.files?.[0])} />
          </label>
          {!preview && <p className="text-xs text-ink/50 mt-1">Sans logo, le nom du partenaire s'affiche à la place.</p>}
        </div>

        {error && <p role="alert" className="text-sm font-semibold text-red-600">{error}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary px-5 py-2">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary px-5 py-2 disabled:opacity-60">
            {saving ? 'Enregistrement...' : partner ? 'Mettre à jour' : 'Ajouter'}
          </button>
        </div>
      </form>
    </div>
  );
};

const PartnersAdmin = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    getPartners()
      .then(setPartners)
      .catch(() => setError('Erreur lors du chargement des partenaires'))
      .finally(() => setLoading(false));
  }, []);

  const sortByOrder = (list: Partner[]) => [...list].sort((a, b) => a.order - b.order);
  const nextOrder = partners.reduce((max, p) => Math.max(max, p.order), 0) + 1;

  const handleSubmit = async (data: PartnerFormData) => {
    if (editing) {
      const updated = await updatePartner(editing, data);
      setPartners(prev => sortByOrder(prev.map(p => (p.id === updated.id ? updated : p))));
    } else {
      const created = await addPartner(data);
      setPartners(prev => sortByOrder([...prev, created]));
    }
    setFormOpen(false);
    setEditing(null);
  };

  const handleDelete = async (partner: Partner) => {
    if (!window.confirm(`Supprimer ${partner.name} ?`)) return;
    try {
      await deletePartner(partner);
      setPartners(prev => prev.filter(p => p.id !== partner.id));
    } catch {
      setError('Erreur lors de la suppression du partenaire');
    }
  };

  const importDefaults = async () => {
    setImporting(true);
    setError(null);
    try {
      const created: Partner[] = [];
      for (const partner of defaultPartners) {
        created.push(await addPartner({
          name: partner.name,
          url: partner.url,
          category: partner.category,
          order: partner.order,
          logo: null,
          logoPath: partner.logo,
        }));
      }
      setPartners(sortByOrder(created));
    } catch {
      setError("L'import a échoué en cours de route : vérifiez la liste puis complétez à la main.");
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <p className="p-6 text-center text-brand-pink-600">Chargement...</p>;
  }

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-semibold text-ink">Partenaires ({partners.length})</h2>
        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="btn-primary px-4 py-2 text-sm"
        >
          <PlusIcon className="h-5 w-5" /> Nouveau partenaire
        </button>
      </div>

      {error && <p className="mb-4 text-sm font-semibold text-red-600">{error}</p>}

      {partners.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-ink/20 rounded-xl">
          <p className="text-ink/70 mb-4">
            Aucun partenaire enregistré. Le site affiche pour l'instant la liste par défaut ({defaultPartners.length} partenaires).
          </p>
          <button onClick={importDefaults} disabled={importing} className="btn-primary px-5 py-2 disabled:opacity-60">
            {importing ? 'Import...' : 'Importer les partenaires par défaut'}
          </button>
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map(partner => (
            <li key={partner.id} className="flex items-center gap-4 border border-ink/10 rounded-xl p-3">
              <div className="h-14 w-20 shrink-0 flex items-center justify-center bg-white rounded-lg p-1">
                {partner.logo ? (
                  <img src={partner.logo} alt="" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-[10px] text-gray-500 text-center">sans logo</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink truncate">{partner.name}</p>
                <p className="text-xs text-ink/60">#{partner.order} · {categories.find(c => c.value === partner.category)?.label}</p>
              </div>
              <button onClick={() => { setEditing(partner); setFormOpen(true); }} className="p-1 text-accent" aria-label={`Modifier ${partner.name}`}>
                <PencilIcon className="h-5 w-5" />
              </button>
              <button onClick={() => handleDelete(partner)} className="p-1 text-red-500" aria-label={`Supprimer ${partner.name}`}>
                <TrashIcon className="h-5 w-5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {formOpen && (
        <PartnerForm
          partner={editing}
          nextOrder={nextOrder}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};

export default PartnersAdmin;
